// Wrapper temporario — usa apiFetch direto para PATCH /clients/:id com os campos editaveis.
// Se ja houver um ClientFormDialog completo no ClientsPage, refatorar para extrair pra ca.

import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import type { ClientDetail } from '../ClientDetailPage';

import { ApiError, apiFetch } from '@/lib/api';


export function ClientFormDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: ClientDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    codigoCliente: initial.codigoCliente ?? '',
    razaoSocial: initial.razaoSocial,
    nomeFantasia: initial.nomeFantasia ?? '',
    inscricaoMunicipal: initial.inscricaoMunicipal ?? '',
    email: initial.email ?? '',
    telefone: initial.telefone ?? '',
    enderecoLogradouro: initial.enderecoLogradouro ?? '',
    enderecoNumero: initial.enderecoNumero ?? '',
    enderecoComplemento: initial.enderecoComplemento ?? '',
    enderecoBairro: initial.enderecoBairro ?? '',
    enderecoCidade: initial.enderecoCidade,
    enderecoUf: initial.enderecoUf,
    enderecoCep: initial.enderecoCep ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/clients/${initial.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          codigoCliente: form.codigoCliente || undefined,
          razaoSocial: form.razaoSocial,
          nomeFantasia: form.nomeFantasia || undefined,
          inscricaoMunicipal: form.inscricaoMunicipal || undefined,
          email: form.email || undefined,
          telefone: form.telefone ? form.telefone.replace(/\D/g, '') : undefined,
          enderecoLogradouro: form.enderecoLogradouro || undefined,
          enderecoNumero: form.enderecoNumero || undefined,
          enderecoComplemento: form.enderecoComplemento || undefined,
          enderecoBairro: form.enderecoBairro || undefined,
          enderecoCidade: form.enderecoCidade,
          enderecoUf: form.enderecoUf,
          enderecoCep: form.enderecoCep ? form.enderecoCep.replace(/\D/g, '') : undefined,
        }),
      }),
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-medium">Editar cliente — {initial.razaoSocial}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Razão social *">
              <input
                required
                className="input"
                value={form.razaoSocial}
                onChange={(e) => setForm((s) => ({ ...s, razaoSocial: e.target.value }))}
              />
            </Field>
            <Field label="Nome fantasia">
              <input
                className="input"
                value={form.nomeFantasia}
                onChange={(e) => setForm((s) => ({ ...s, nomeFantasia: e.target.value }))}
              />
            </Field>
            <Field label="Código interno">
              <input
                className="input font-mono"
                value={form.codigoCliente}
                onChange={(e) => setForm((s) => ({ ...s, codigoCliente: e.target.value }))}
              />
            </Field>
            <Field label="Inscrição municipal">
              <input
                className="input font-mono"
                value={form.inscricaoMunicipal}
                onChange={(e) => setForm((s) => ({ ...s, inscricaoMunicipal: e.target.value }))}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                className="input"
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              />
            </Field>
            <Field label="Telefone">
              <input
                className="input"
                value={form.telefone}
                onChange={(e) => setForm((s) => ({ ...s, telefone: e.target.value }))}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_80px_1fr]">
            <Field label="Logradouro">
              <input
                className="input"
                value={form.enderecoLogradouro}
                onChange={(e) => setForm((s) => ({ ...s, enderecoLogradouro: e.target.value }))}
              />
            </Field>
            <Field label="Nº">
              <input
                className="input"
                value={form.enderecoNumero}
                onChange={(e) => setForm((s) => ({ ...s, enderecoNumero: e.target.value }))}
              />
            </Field>
            <Field label="Complemento">
              <input
                className="input"
                value={form.enderecoComplemento}
                onChange={(e) => setForm((s) => ({ ...s, enderecoComplemento: e.target.value }))}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_2fr_80px_1fr]">
            <Field label="Bairro">
              <input
                className="input"
                value={form.enderecoBairro}
                onChange={(e) => setForm((s) => ({ ...s, enderecoBairro: e.target.value }))}
              />
            </Field>
            <Field label="Cidade *">
              <input
                required
                className="input"
                value={form.enderecoCidade}
                onChange={(e) => setForm((s) => ({ ...s, enderecoCidade: e.target.value }))}
              />
            </Field>
            <Field label="UF *">
              <input
                required
                maxLength={2}
                className="input"
                value={form.enderecoUf}
                onChange={(e) =>
                  setForm((s) => ({ ...s, enderecoUf: e.target.value.toUpperCase() }))
                }
              />
            </Field>
            <Field label="CEP">
              <input
                className="input font-mono"
                value={form.enderecoCep}
                onChange={(e) => setForm((s) => ({ ...s, enderecoCep: e.target.value }))}
              />
            </Field>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {save.isPending ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>
        </form>

        <style>{`
          .input {
            width: 100%;
            border: 1px solid hsl(var(--input));
            background: hsl(var(--background));
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.875rem;
          }
          .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}
